// Risk Assessment rubric — pulled from the program's source workbook
// (May 1, 2026.xlsx). Three categories × five levels each. Total possible 15.

export const RISK_LEVELS = [
  { value: 1, label: "Negligible", color: "bg-green-100 text-green-800 border-green-200" },
  { value: 2, label: "Minor",      color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: 3, label: "Moderate",   color: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: 4, label: "Major",      color: "bg-orange-100 text-orange-800 border-orange-200" },
  { value: 5, label: "Extreme",    color: "bg-red-100 text-red-800 border-red-200" },
];

export const RISK_CATEGORIES = [
  {
    key: "injury_illness",
    label: "Injury / Illness Issues",
    descriptions: {
      1: "Minor Injuries or illnesses (to students and/or staff) that do not require direct intervention of nursing or medical staff.",
      2: "Minor injuries or illnesses (to students and/or staff) that require first aid treatment and/or brief medical intervention; injuries or illnesses that cause minor emotional distress.",
      3: "Significant injuries or illnesses (to students and/or staff) that require direct medical treatment; 5 or fewer Incident Reports for the week; emotional distress requiring staff intervention.",
      4: "Significant injuries or illnesses requiring urgent care; 5 or more Incident Reports for the week; emotional distress that results in missed participation in the routine.",
      5: "Incidents or illnesses that lead to death or major permanent incapacity; events impacting members of the public or requiring emergency responders; physical/emotional trauma.",
    },
  },
  {
    key: "behavioral",
    label: "General Safety / Behavioral Issues",
    descriptions: {
      1: "Minor event(s) that require simple and infrequent intervention; mental health or behavioral issues clearly identified and successfully managed.",
      2: "Minor event(s) requiring simple indirect intervention; report of possible self-harm or abuse; non-specific threats; ongoing support handled via embedded supports.",
      3: "Fewer than 3 events requiring direct staff intervention and alternative approaches; self-report of self-harm thoughts; one DCF-referred abuse incident (<3 staff hours); student leaves and returns independently.",
      4: "More than 3 events requiring direct intervention plus ongoing 1:1 support; direct evidence of self-harm; multiple abuse incidents (>4 staff hours, DCF intervention); student leaves and does not return within 30 minutes (LEA/guardian notified); multiple BIP changes needed.",
      5: "More than two separate events requiring one-to-one staff intervention for >2 hours and involvement of emergency or outside agency services.",
    },
  },
  {
    key: "cultural",
    label: "Culture / Environmental Issues",
    descriptions: {
      1: "Event(s) between students or with staff easily managed; routine adjustments easily managed; transportation alternatives needed but resolved.",
      2: "Difficulties needing more than one staff to support; routine adjustments for 1 student for a full day; insufficient resources blocking an outing/project; >1 student needs program transport.",
      3: "Difficulties requiring multiple staff + clinical support; routine adjustments for 2–3 days; insufficient resources blocking >1 student; insufficient food causing missed meal; transportation fails ≤3 occasions.",
      4: "Difficulties requiring multiple staff + clinical support for a full day affecting program participation; chaotic environment for multiple days needing majority of staff; cancellation of an outing/project; insufficient food causing multiple missed meals; transportation fails ≥4 occasions.",
      5: "Events resulting in imminent risk to self or others; chaotic environment requiring all staff to maintain order for 3+ days.",
    },
  },
];

export const TOTAL_BANDS = [
  { max: 3,  label: "Low",       color: "bg-green-100 text-green-800" },
  { max: 6,  label: "Moderate",  color: "bg-amber-100 text-amber-800" },
  { max: 9,  label: "Elevated",  color: "bg-orange-100 text-orange-800" },
  { max: 15, label: "High",      color: "bg-red-100 text-red-800" },
];

// Default color palette applied by band order (low -> high).
const BAND_PALETTE = [
  "bg-green-100 text-green-800",
  "bg-amber-100 text-amber-800",
  "bg-orange-100 text-orange-800",
  "bg-red-100 text-red-800",
];

// Active bands can be overridden at runtime from the admin Risk Assessment settings.
let activeBands = TOTAL_BANDS;

export function setRiskBands(bands) {
  if (!Array.isArray(bands) || bands.length === 0) return;
  const sorted = [...bands].sort((a, b) => a.max - b.max);
  activeBands = sorted.map((b, i) => ({
    max: b.max,
    label: b.label,
    color: b.color || BAND_PALETTE[Math.min(i, BAND_PALETTE.length - 1)],
  }));
}

export function getRiskBands() {
  return activeBands;
}

export function totalBand(total) {
  if (total == null) return null;
  return activeBands.find((b) => total <= b.max) || activeBands[activeBands.length - 1];
}
